/**
 * markdown.ts — agreement markdown -> sanitised HTML for the on-screen review.
 *
 * MIRRORS `fleet-backend/supabase/functions/generate-agreement/render/markdown.ts`. The
 * PDF renderer folds the same source into a block tree it can measure; this file folds it
 * into HTML a browser can paint. What must not differ is everything BEFORE the fold:
 * front matter off, HTML comments out, `:::` containers split, then markdown-it 14 with
 * the same options and the same preset. The regexes and the option object below are
 * copied from that module for exactly that reason — if the two disagree about where a
 * container starts, the admin reviews one document and the driver signs another.
 *
 * ── WHAT THE TEMPLATES USE ────────────────────────────────────────────────────
 * headings (# .. ###) · paragraphs · ordered and unordered lists · GFM tables ·
 * `**bold**` inline clause numbers · `{{merge.field}}` marks · HTML comments (dropped)
 * · and seven containers:
 *
 *   :::pagebreak:::            a page boundary in the PDF; a ruled divider on screen
 *   :::keeptogether ... :::    never straddles a page in the PDF; a plain group on screen
 *   :::signature role=X ... :::keeps together AND reserves the ink slot for party X
 *   :::particulars ... :::     exactly one GFM table, drawn as the schedule grid
 *   :::box ... :::             whatever it holds, inside a bordered panel
 *   :::included ... :::        exactly one UL, its markers drawn as ticks
 *   :::excluded ... :::        exactly one UL, its markers drawn as crosses
 *
 * THE LAST FOUR ARE PRESENTATION, AND THE CONTRACT IS THE FENCE, NOT THE MARKUP. The PDF
 * renderer draws the same four from the same fences; what each side emits is its own medium's
 * (a measured block tree there, a classed wrapper here), so the parity that matters is which
 * names exist and what each one is allowed to wrap — stated above, pinned by the tests, and
 * identical in both modules. A template that fences a paragraph as `:::particulars` is a
 * template defect, not a renderer branch: neither side enforces the shape, both draw what
 * they are given.
 *
 * ── DELIBERATE NON-FEATURES ───────────────────────────────────────────────────
 * 1. NO HTML. Comments are stripped before parsing (the transcriptions carry
 *    `<!-- TRANSCRIPTION-UNCERTAIN -->` notes that must never reach a signed page) and
 *    markdown-it runs with `html: false`, so any other tag prints as inert text.
 *    DOMPurify then runs over the result: markdown-it's escaping is the guarantee, and
 *    the sanitiser is the belt for the day a merge value arrives pre-rendered.
 * 2. NO AUTO-NUMBERING for normative clauses. Clause numbers are literal `**1.1**`
 *    runs in the template — a renderer that renumbers a legal document is a defect.
 * 3. NO LINKS OR IMAGES. Both are stripped by the sanitiser allow-list below. A document
 *    under review cannot carry a live target honestly.
 */

import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';

import { MARK_END, MARK_MISSING, MARK_VALUE } from './merge';

// ─── markdown-it configuration (shared with the PDF renderer) ───────────────

export const MARKDOWN_IT_OPTIONS = {
    html: false,
    linkify: false,
    typographer: false,
    breaks: false,
} as const;

/**
 * The `default` preset is what carries GFM tables; commonmark would drop them.
 *
 * The five overridden rules are how this renderer reproduces the PDF folder's deliberate
 * non-features: a link keeps its text and loses its target, an image is dropped entirely,
 * and raw HTML never reaches the page. Doing it here rather than leaving it to the
 * sanitiser means the guarantee is deterministic and identical in every environment,
 * and DOMPurify below stays what it should be — a second line, not the only one.
 */
export function createMarkdownIt(): MarkdownIt {
    const md = new MarkdownIt('default', { ...MARKDOWN_IT_OPTIONS });
    const drop = () => '';
    md.renderer.rules.link_open = drop;
    md.renderer.rules.link_close = drop;
    md.renderer.rules.image = drop;
    md.renderer.rules.html_block = drop;
    md.renderer.rules.html_inline = drop;
    return md;
}

let cachedMd: MarkdownIt | null = null;
function sharedMarkdownIt(): MarkdownIt {
    if (!cachedMd) cachedMd = createMarkdownIt();
    return cachedMd;
}

// ─── Source preparation ──────────────────────────────────────────────────────

const HTML_COMMENT = /<!--[\s\S]*?-->/g;

/** Drops every HTML comment. Templates carry transcription notes that must not print. */
export function stripHtmlComments(source: string): string {
    return source.replace(HTML_COMMENT, '');
}

export interface FrontMatterSplit {
    /** Raw front-matter text, without the fences. Empty when there is none. */
    frontMatter: string;
    body: string;
}

/**
 * Splits a leading `---` front-matter block off a template body.
 *
 * Documents rendered from the database arrive without one — `agreement_templates` holds
 * the front matter as columns — but splitting unconditionally costs nothing and makes a
 * pasted template file safe to preview too.
 */
export function splitFrontMatter(source: string): FrontMatterSplit {
    const normalized = source.replace(/^\uFEFF/, '');
    const match = /^---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/.exec(normalized);
    if (!match) return { frontMatter: '', body: normalized };
    return { frontMatter: match[1], body: normalized.slice(match[0].length) };
}

// ─── Container tree ──────────────────────────────────────────────────────────

const CONTAINER_PAGEBREAK = /^:::\s*pagebreak\s*:::\s*$/;
const CONTAINER_OPEN = /^:::\s*([A-Za-z][A-Za-z0-9_-]*)\s*(?:role\s*=\s*([A-Za-z][A-Za-z0-9_]*))?\s*$/;
const CONTAINER_CLOSE = /^:::\s*$/;

type ContainerNode =
    | { type: 'markdown'; text: string }
    | { type: 'pagebreak' }
    | { type: 'container'; name: string; role: string | null; children: ContainerNode[] };

/**
 * Splits `:::` containers out of the source BEFORE markdown-it sees it, so a container
 * fence can never be mistaken for a paragraph and nesting (a signature block inside a
 * keeptogether) works without a plugin. An unknown container name is transparent: its
 * children render inline. An unclosed container closes at end of input.
 */
function parseContainers(source: string): ContainerNode[] {
    const lines = source.split(/\r?\n/);
    const root: ContainerNode[] = [];
    const stack: ContainerNode[][] = [root];
    let buffer: string[] = [];

    const flush = () => {
        if (buffer.length === 0) return;
        const text = buffer.join('\n');
        if (text.trim().length > 0) stack[stack.length - 1].push({ type: 'markdown', text });
        buffer = [];
    };

    for (const line of lines) {
        if (CONTAINER_PAGEBREAK.test(line)) {
            flush();
            stack[stack.length - 1].push({ type: 'pagebreak' });
            continue;
        }
        const open = CONTAINER_OPEN.exec(line);
        if (open) {
            flush();
            const node: ContainerNode = {
                type: 'container',
                name: open[1].toLowerCase(),
                role: open[2] ? open[2].toUpperCase() : null,
                children: [],
            };
            stack[stack.length - 1].push(node);
            stack.push(node.children);
            continue;
        }
        if (CONTAINER_CLOSE.test(line)) {
            flush();
            if (stack.length > 1) stack.pop();
            continue;
        }
        buffer.push(line);
    }
    flush();
    return root;
}

// ─── Marks -> spans ──────────────────────────────────────────────────────────

/**
 * Turns the merge marks into spans the stylesheet can tint.
 *
 * Runs on the rendered HTML rather than on the markdown because markdown-it would escape
 * the tags. It is safe there: `sanitizeMergeValue` flattens a value to one line and
 * escapes every markdown-active punctuation character, so a marked run can never span an
 * element boundary — the pair always opens and closes inside one text node. Anything that
 * still looks unbalanced (a stray opener with no closer) has its mark dropped rather than
 * being allowed to emit an unterminated tag.
 */
function markSpans(html: string): string {
    const withValues = html.replace(
        new RegExp(`${MARK_VALUE}([^${MARK_END}]*)${MARK_END}`, 'g'),
        (_m, inner: string) => `<span class="agr-mf">${inner}</span>`,
    );
    const withMissing = withValues.replace(
        new RegExp(`${MARK_MISSING}([^${MARK_END}]*)${MARK_END}`, 'g'),
        (_m, inner: string) => `<span class="agr-mf-empty">${inner}</span>`,
    );
    return withMissing.replace(new RegExp(`[${MARK_VALUE}${MARK_MISSING}${MARK_END}]`, 'g'), '');
}

// ─── Sanitiser ───────────────────────────────────────────────────────────────

/**
 * A legal document is prose, tables and rules — nothing else. Anchors and images are
 * absent from the list on purpose (see non-feature 3), which is what strips them.
 *
 * THE FOUR NEW CONTAINERS ADD NOTHING TO IT, WHICH IS THE DESIGN. Each folds into a `div`
 * carrying one class, and that element was already here; the ticks and crosses of
 * `:::included` / `:::excluded` are drawn by the stylesheet — an inline SVG data URI on a
 * pseudo-element — rather than by an `<svg>` in the document. Admitting a tag with its own
 * attribute surface, its own script vectors and its own sanitiser configuration, in order to
 * draw two 16px marks, would spend the narrowness this list exists for.
 *
 * EXPORTED SO THE TESTS CAN CHECK EMISSION AGAINST IT. DOMPurify needs a DOM and there is none
 * under vitest's node environment (see `sanitize` below), so a suite that renders a container
 * and asserts it survives asserts nothing. Comparing what the renderer emits against the very
 * list the browser sanitises with is the check that actually holds.
 */
export const ALLOWED_TAGS = [
    'p', 'br', 'hr', 'strong', 'em', 'span', 'div', 'section',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'blockquote', 'code', 'pre',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
];

export const ALLOWED_ATTR = ['class', 'style', 'data-role', 'colspan', 'rowspan'];

/**
 * The second line.
 *
 * markdown-it already runs with `html: false` and with links, images and raw HTML dropped
 * at the renderer, so the string handed here carries only the vocabulary above. DOMPurify
 * is what makes that a guarantee rather than an argument.
 *
 * IT NEEDS A DOM, AND OUTSIDE A BROWSER THERE IS NONE. `DOMPurify.sanitize` is simply
 * absent when the module initialises without a `window` — a unit test, where the string is
 * asserted against and never rendered. Passing the markup through there is safe for that
 * reason and no other; anything that renders this output runs in a browser, where the
 * sanitiser is present and this branch is unreachable.
 */
function sanitize(html: string): string {
    if (typeof DOMPurify.sanitize !== 'function') return html;
    return DOMPurify.sanitize(html, {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
        // `style` survives only so markdown-it's own table alignment (`text-align:…`)
        // does; nothing in the pipeline emits any other declaration.
        ALLOW_DATA_ATTR: false,
    });
}

// ─── Entry point ─────────────────────────────────────────────────────────────

function escapeAttr(value: string): string {
    return value.replace(/[&<>"']/g, (ch) => {
        switch (ch) {
            case '&': return '&amp;';
            case '<': return '&lt;';
            case '>': return '&gt;';
            case '"': return '&quot;';
            default: return '&#39;';
        }
    });
}

/**
 * The containers that fold into one classed wrapper, and the element each takes.
 *
 * `signature` is not here because it is the only one that carries an attribute — its party —
 * and it is handled in `renderNodes` for that reason alone.
 *
 * THE FOUR NEW WRAPPERS ARE `div.agr-*`, THE SAME FOUR CLASS NAMES THE DRIVER APP EMITS. Both
 * apps show a driver-facing instrument and both style it from these hooks, so the vocabulary is
 * cross-repo and a `<section>` here — however well it described the schedule as a region — would
 * make one stylesheet's selectors quietly wrong against the other's markup.
 *
 * THE TWO OLD ONES ARE NOT TOUCHED, and their divergence is left where it is: this app's
 * `keeptogether` is `agr-keeptogether` and the driver's is `agr-keep`. They predate the shared
 * vocabulary, each app's stylesheet already matches its own, and renaming a class to tidy the
 * pair up would restyle documents in production to fix nothing anyone can see.
 */
const WRAPPED_CONTAINERS: Readonly<Record<string, { tag: 'div'; className: string }>> = {
    keeptogether: { tag: 'div', className: 'agr-keeptogether' },
    particulars: { tag: 'div', className: 'agr-particulars' },
    box: { tag: 'div', className: 'agr-box' },
    included: { tag: 'div', className: 'agr-included' },
    excluded: { tag: 'div', className: 'agr-excluded' },
};

function renderNodes(nodes: ContainerNode[], md: MarkdownIt): string {
    let html = '';
    for (const node of nodes) {
        if (node.type === 'pagebreak') {
            html += '<hr class="agr-pagebreak" />';
            continue;
        }
        if (node.type === 'markdown') {
            html += md.render(node.text);
            continue;
        }
        const inner = renderNodes(node.children, md);
        if (node.name === 'signature') {
            html += `<section class="agr-signature"${node.role ? ` data-role="${escapeAttr(node.role)}"` : ''
                }>${inner}</section>`;
            continue;
        }
        // AN OWN-PROPERTY CHECK, NOT A BARE LOOKUP. The fence name is whatever the template
        // author typed, and the open regex admits any `[A-Za-z][A-Za-z0-9_-]*` — which includes
        // `constructor`, `toString`, `valueOf` and `hasOwnProperty`. A plain
        // `WRAPPED_CONTAINERS[name]` finds those on `Object.prototype` and returns something
        // truthy with no `tag` and no `className`, so a `:::constructor` fence would emit
        // `<undefined class="undefined">` instead of the transparent fallback below. The old
        // `else if (node.name === 'keeptogether')` compared strings and could not do this; the
        // table lookup that replaced it can, so the own-property check comes with it.
        //
        // Spelled `Object.prototype.hasOwnProperty.call` rather than `Object.hasOwn` because
        // this project targets ES2020 and `Object.hasOwn` is ES2022 — it runs everywhere the
        // app ships, but it is not in the `lib` tsc checks against.
        const wrapper = Object.prototype.hasOwnProperty.call(WRAPPED_CONTAINERS, node.name)
            ? WRAPPED_CONTAINERS[node.name]
            : undefined;
        if (wrapper) {
            html += `<${wrapper.tag} class="${wrapper.className}">${inner}</${wrapper.tag}>`;
        } else {
            // Unknown container: transparent, so an unrecognised fence never eats content.
            html += inner;
        }
    }
    return html;
}

/**
 * Parses agreement markdown (already merged) into sanitised HTML.
 *
 * Order matters and is the PDF renderer's order: front matter off, comments out,
 * containers split, markdown-it, marks to spans, sanitise. Each step removes a shape the
 * next one would misread.
 */
export function renderAgreementHtml(source: string, md: MarkdownIt = sharedMarkdownIt()): string {
    const { body } = splitFrontMatter(source ?? '');
    const html = renderNodes(parseContainers(stripHtmlComments(body)), md);
    return sanitize(markSpans(html));
}
